import React, { useState, useEffect } from 'react';
import { Truck, Plus, Edit3, X, Check, Trash2, Search } from 'lucide-react';
import { api } from '../../utils/api';
import { toast } from '../ui/simple-toaster';

const TRAILER_TYPES = [
  { value: 'SEMI', label: 'Semi Trailer' },
  { value: 'DOG', label: 'Dog Trailer' },
  { value: 'B_DOUBLE_LEAD', label: 'B-Double Lead' },
  { value: 'B_DOUBLE_TAG', label: 'B-Double Tag' },
  { value: 'PIG', label: 'Pig Trailer' },
  { value: 'OTHER', label: 'Other' },
];

const emptyForm = {
  registration: '', trailer_type: 'SEMI', make: '', model: '',
  year: '', tare_weight_kg: '', gvm_kg: '', axle_count: '', notes: '',
};

const TrailersPanel = () => {
  const [trailers, setTrailers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [editForm, setEditForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => { loadTrailers(); }, []);

  const loadTrailers = async () => {
    setLoading(true);
    try {
      const res = await api('/trailers');
      setTrailers(res.trailers || []);
    } catch {
      toast.error('Failed to load trailers');
    } finally { setLoading(false); }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.registration.trim()) { toast.error('Registration is required'); return; }
    setSaving(true);
    try {
      const payload = {
        registration: form.registration.trim(),
        trailer_type: form.trailer_type,
        make: form.make.trim() || null,
        model: form.model.trim() || null,
        year: form.year ? parseInt(form.year) : null,
        tare_weight_kg: form.tare_weight_kg ? parseFloat(form.tare_weight_kg) : null,
        gvm_kg: form.gvm_kg ? parseFloat(form.gvm_kg) : null,
        axle_count: form.axle_count ? parseInt(form.axle_count) : null,
        notes: form.notes.trim() || null,
      };
      await api('/trailers', { method: 'POST', body: JSON.stringify(payload) });
      toast.success(`Trailer ${payload.registration} registered`);
      setForm({ ...emptyForm });
      setShowAdd(false);
      loadTrailers();
    } catch (err) { toast.error('Failed to create trailer: ' + err.message); }
    finally { setSaving(false); }
  };

  const startEdit = (trailer) => {
    setEditingId(trailer.trailer_id);
    setEditForm({
      registration: trailer.registration || '',
      trailer_type: trailer.trailer_type || 'SEMI',
      make: trailer.make || '',
      model: trailer.model || '',
      year: trailer.year || '',
      tare_weight_kg: trailer.tare_weight_kg || '',
      gvm_kg: trailer.gvm_kg || '',
      axle_count: trailer.axle_count || '',
      notes: trailer.notes || '',
    });
  };

  const handleUpdate = async () => {
    setSaving(true);
    try {
      const payload = {
        registration: editForm.registration.trim() || undefined,
        trailer_type: editForm.trailer_type || undefined,
        make: editForm.make.trim() || null,
        model: editForm.model.trim() || null,
        year: editForm.year ? parseInt(editForm.year) : null,
        tare_weight_kg: editForm.tare_weight_kg ? parseFloat(editForm.tare_weight_kg) : null,
        gvm_kg: editForm.gvm_kg ? parseFloat(editForm.gvm_kg) : null,
        axle_count: editForm.axle_count ? parseInt(editForm.axle_count) : null,
        notes: editForm.notes.trim() || null,
      };
      await api(`/trailers/${editingId}`, { method: 'PATCH', body: JSON.stringify(payload) });
      toast.success('Trailer updated');
      setEditingId(null);
      loadTrailers();
    } catch (err) { toast.error('Failed to update: ' + err.message); }
    finally { setSaving(false); }
  };

  const handleDeactivate = async (trailerId, rego) => {
    if (!window.confirm(`Deactivate trailer ${rego}?`)) return;
    try {
      await api(`/trailers/${trailerId}`, { method: 'DELETE' });
      toast.success(`Trailer ${rego} deactivated`);
      loadTrailers();
    } catch (err) { toast.error('Failed to deactivate: ' + err.message); }
  };

  const filtered = trailers.filter(t => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (t.registration || '').toLowerCase().includes(q)
      || (t.make || '').toLowerCase().includes(q)
      || (t.model || '').toLowerCase().includes(q)
      || (t.trailer_type || '').toLowerCase().includes(q);
  });

  const typeLabel = (val) => TRAILER_TYPES.find(t => t.value === val)?.label || val;

  return (
    <div className="space-y-4" data-testid="trailers-panel">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-white">Trailers</h2>
          <p className="text-slate-400 text-xs mt-0.5">{trailers.length} registered</p>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-48">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              type="text" placeholder="Search trailers..."
              value={search} onChange={e => setSearch(e.target.value)}
              className="w-full pl-8 pr-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm"
              data-testid="trailer-search"
            />
          </div>
          <button
            onClick={() => { setShowAdd(!showAdd); setForm({ ...emptyForm }); }}
            className="flex items-center gap-1.5 px-3 py-2 bg-emerald-500 text-white rounded-lg text-sm hover:bg-emerald-600"
            data-testid="add-trailer-btn"
          >
            <Plus className="w-4 h-4" /> Add Trailer
          </button>
        </div>
      </div>

      {/* Add Form */}
      {showAdd && (
        <form onSubmit={handleAdd} className="bg-slate-800 border border-slate-700 rounded-xl p-4 space-y-3" data-testid="add-trailer-form">
          <h3 className="text-white font-medium text-sm">Register New Trailer</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Registration *</label>
              <input type="text" value={form.registration}
                onChange={e => setForm({ ...form, registration: e.target.value })}
                className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm"
                placeholder="e.g. TR-1234" required data-testid="trailer-rego-input" />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Type</label>
              <select value={form.trailer_type}
                onChange={e => setForm({ ...form, trailer_type: e.target.value })}
                className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm"
                data-testid="trailer-type-select">
                {TRAILER_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Make</label>
              <input type="text" value={form.make}
                onChange={e => setForm({ ...form, make: e.target.value })}
                className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm"
                placeholder="e.g. Vawdrey" data-testid="trailer-make-input" />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Model</label>
              <input type="text" value={form.model}
                onChange={e => setForm({ ...form, model: e.target.value })}
                className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm"
                placeholder="e.g. Drop Deck" data-testid="trailer-model-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Year</label>
              <input type="number" value={form.year}
                onChange={e => setForm({ ...form, year: e.target.value })}
                className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm"
                placeholder="2024" data-testid="trailer-year-input" />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Tare (kg)</label>
              <input type="number" value={form.tare_weight_kg}
                onChange={e => setForm({ ...form, tare_weight_kg: e.target.value })}
                className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm"
                placeholder="e.g. 7500" data-testid="trailer-tare-input" />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">GVM (kg)</label>
              <input type="number" value={form.gvm_kg}
                onChange={e => setForm({ ...form, gvm_kg: e.target.value })}
                className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm"
                placeholder="e.g. 20000" data-testid="trailer-gvm-input" />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Axles</label>
              <input type="number" value={form.axle_count}
                onChange={e => setForm({ ...form, axle_count: e.target.value })}
                className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm"
                placeholder="e.g. 3" data-testid="trailer-axle-input" />
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Notes</label>
            <input type="text" value={form.notes}
              onChange={e => setForm({ ...form, notes: e.target.value })}
              className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm"
              placeholder="Optional notes" data-testid="trailer-notes-input" />
          </div>
          <div className="flex gap-2 pt-1">
            <button type="button" onClick={() => setShowAdd(false)}
              className="px-4 py-2 bg-slate-700 text-white rounded-lg text-sm">Cancel</button>
            <button type="submit" disabled={saving}
              className="px-4 py-2 bg-emerald-500 text-white rounded-lg text-sm disabled:opacity-50"
              data-testid="save-trailer-btn">
              {saving ? 'Saving...' : 'Register Trailer'}
            </button>
          </div>
        </form>
      )}

      {/* Trailer List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-2 border-slate-600 border-t-emerald-500 rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-slate-500">
          <Truck className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p className="text-sm">{search ? 'No trailers match your search' : 'No trailers registered yet'}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(trailer => (
            <div key={trailer.trailer_id}
              className="bg-slate-800 border border-slate-700 rounded-xl p-3 sm:p-4"
              data-testid={`trailer-row-${trailer.trailer_id}`}
            >
              {editingId === trailer.trailer_id ? (
                /* Edit Mode */
                <div className="space-y-3">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div>
                      <label className="text-xs text-slate-400 mb-1 block">Registration</label>
                      <input type="text" value={editForm.registration}
                        onChange={e => setEditForm({ ...editForm, registration: e.target.value })}
                        className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm"
                        data-testid="edit-trailer-rego" />
                    </div>
                    <div>
                      <label className="text-xs text-slate-400 mb-1 block">Type</label>
                      <select value={editForm.trailer_type}
                        onChange={e => setEditForm({ ...editForm, trailer_type: e.target.value })}
                        className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm"
                        data-testid="edit-trailer-type">
                        {TRAILER_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-slate-400 mb-1 block">Make</label>
                      <input type="text" value={editForm.make}
                        onChange={e => setEditForm({ ...editForm, make: e.target.value })}
                        className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm"
                        data-testid="edit-trailer-make" />
                    </div>
                    <div>
                      <label className="text-xs text-slate-400 mb-1 block">Model</label>
                      <input type="text" value={editForm.model}
                        onChange={e => setEditForm({ ...editForm, model: e.target.value })}
                        className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm"
                        data-testid="edit-trailer-model" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div>
                      <label className="text-xs text-slate-400 mb-1 block">Year</label>
                      <input type="number" value={editForm.year}
                        onChange={e => setEditForm({ ...editForm, year: e.target.value })}
                        className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm" />
                    </div>
                    <div>
                      <label className="text-xs text-slate-400 mb-1 block">Tare (kg)</label>
                      <input type="number" value={editForm.tare_weight_kg}
                        onChange={e => setEditForm({ ...editForm, tare_weight_kg: e.target.value })}
                        className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm" />
                    </div>
                    <div>
                      <label className="text-xs text-slate-400 mb-1 block">GVM (kg)</label>
                      <input type="number" value={editForm.gvm_kg}
                        onChange={e => setEditForm({ ...editForm, gvm_kg: e.target.value })}
                        className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm" />
                    </div>
                    <div>
                      <label className="text-xs text-slate-400 mb-1 block">Axles</label>
                      <input type="number" value={editForm.axle_count}
                        onChange={e => setEditForm({ ...editForm, axle_count: e.target.value })}
                        className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm" />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setEditingId(null)}
                      className="px-3 py-1.5 bg-slate-700 text-white rounded-lg text-sm">
                      <X className="w-4 h-4" />
                    </button>
                    <button onClick={handleUpdate} disabled={saving}
                      className="px-3 py-1.5 bg-emerald-500 text-white rounded-lg text-sm disabled:opacity-50"
                      data-testid="confirm-edit-trailer">
                      <Check className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ) : (
                /* View Mode */
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Truck className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-white font-medium text-sm">{trailer.registration}</p>
                      <p className="text-slate-400 text-xs">
                        {typeLabel(trailer.trailer_type)}
                        {trailer.make && ` · ${trailer.make}`}
                        {trailer.model && ` ${trailer.model}`}
                        {trailer.year && ` (${trailer.year})`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {trailer.gvm_kg && (
                      <span className="text-slate-500 text-xs hidden sm:inline">
                        {Number(trailer.gvm_kg).toLocaleString()} kg
                      </span>
                    )}
                    {trailer.axle_count && (
                      <span className="text-slate-500 text-xs hidden sm:inline">
                        {trailer.axle_count} axle{trailer.axle_count > 1 ? 's' : ''}
                      </span>
                    )}
                    <button onClick={() => startEdit(trailer)}
                      className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg"
                      data-testid={`edit-trailer-${trailer.trailer_id}`}>
                      <Edit3 className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDeactivate(trailer.trailer_id, trailer.registration)}
                      className="p-1.5 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg"
                      data-testid={`delete-trailer-${trailer.trailer_id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TrailersPanel;
